import {useColorTheme} from './color-theme.jsx';
import { useId, useMemo, useState, useRef, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useGrowCycle, lineDataSig, MOTION as PM_MOTION, useEntrance, usePrefersReducedMotion as usePRM, EntranceGroup, useLineDraw } from './motion.jsx';
import {
  PALETTE,
  NEUTRAL,
  SEMANTIC,
  fmtN,
  pieCenterX,
  ChartEmpty,
  hasData,
  useContainerWidth,
  resolveFormatter,
  MultiSeriesTooltip,
  monotonePath,
  tintColor,
  thinLabelIndices,
  xLabelStride,
  SlotLabel,
  peakLabelIndices,
  legendLayout,
  SVGLegendRows,
  // Rule 1 plumbing — single implementation in shared.jsx, reused by
  // both the interactive and the static chart catalogs.
  resolveLabels,
  lastDelta,
  // Axis polish 2026-07: round tick values + label anti-collision +
  // density-adaptive solid line markers (style "C", no white halo).
  niceTicks,
  separateLabelYs,
  lineDotRadius,
  // Adaptive label gutter + 2-line wrap for ranked horizontal bars
  // (feedback 2026-07-14) — single implementation in shared.jsx.
  barLabelLayout,
  // Misma métrica de densidad que la variante estática (shared.jsx),
  // para que las dos no puedan divergir con un ajuste hecho en una sola.
  barDensity,
} from './shared.jsx';

// ── Helpers ──────────────────────────────────────────────────
// `safeMax`/`safeMin` clamp away zero/empty/non-finite results so
// scales never divide by 0 → no `<polyline points="NaN,30">` SVG
// errors when filters narrow the dataset to all-zero rows.
function safeMax(arr) {
  let m = -Infinity;
  for (const v of arr) {
    if (typeof v === 'number' && Number.isFinite(v) && v > m) m = v;
  }
  return Number.isFinite(m) && m > 0 ? m : 1;
}
function safeMin(arr) {
  let m = Infinity;
  for (const v of arr) {
    if (typeof v === 'number' && Number.isFinite(v) && v < m) m = v;
  }
  return Number.isFinite(m) ? m : 0;
}

// Single-point series guard — `i / (n - 1)` produces NaN when n === 1.
// Place lone points in the centre of the chart instead.
function xRatio(i, n) {
  if (n <= 1) return 0.5;
  return i / (n - 1);
}

// Line charts keep their first and last points LINE_INSET px inside the
// plot, so a marker never sits on the Y-axis labels (Evolución,
// 2026-10-26: the first point of every retailer covered the "20" tick).
const LINE_INSET = 10;
function lineX(i, n, left, width) {
  return left + LINE_INSET + xRatio(i, n) * Math.max(0, width - 2 * LINE_INSET);
}

export function useTooltip() {
  const [tip, setTip] = useState(null);
  const svgRef = useRef(null);
  const show = useCallback((e, content) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    setTip({ x: e.clientX - rect.left, y: e.clientY - rect.top, content });
  }, []);
  const hide = useCallback(() => setTip(null), []);
  return { tip, svgRef, show, hide };
}

// Tooltip with edge-clamp + vertical flip, rendered in a PORTAL (rule 17,
// like InfoTip). It used to be an absolute child of the chart wrapper,
// whose `overflow-hidden` (and every scrolling card around it) cut it:
// a six-series tooltip in a compact drill chart is taller than the plot,
// so neither "above" nor "below" fit inside and the bottom rows were
// lost. On `document.body` with `fixed` coordinates it is clamped to the
// VIEWPORT instead: centred over the cursor, above it when there is room,
// below otherwise, and pushed back up when even that would leave the
// screen. `tip.x/y` stay relative to the SVG, as `useTooltip` records them.
export function Tooltip({ tip, svgRef }) {
  const tipRef = useRef(null);
  const [pos, setPos] = useState({ left: 0, top: 0, ready: false });

  useLayoutEffect(() => {
    if (!tip || !svgRef.current || !tipRef.current) {
      setPos(p => ({ ...p, ready: false }));
      return;
    }
    const svgRect = svgRef.current.getBoundingClientRect();
    const tipRect = tipRef.current.getBoundingClientRect();
    const PAD = 6;
    const vw = window.innerWidth || document.documentElement.clientWidth || 0;
    const vh = window.innerHeight || document.documentElement.clientHeight || 0;
    const cx = svgRect.left + tip.x;
    const cy = svgRect.top + tip.y;
    // Horizontal: centred over the cursor, clamped inside the viewport.
    let left = cx - tipRect.width / 2;
    left = Math.max(PAD, Math.min(left, vw - tipRect.width - PAD));
    // Vertical: above the cursor when it fits, else below; never past
    // the bottom edge (then as low as fits, but not above the top).
    let top = cy - tipRect.height - 10;
    if (top < PAD) {
      top = cy + 14;
      if (top + tipRect.height > vh - PAD) top = Math.max(PAD, vh - tipRect.height - PAD);
    }
    setPos({ left, top, ready: true });
  }, [tip, svgRef]);

  if (!tip || !svgRef.current || typeof document === 'undefined') return null;
  return createPortal(
    <div
      ref={tipRef}
      data-chart-tooltip=""
      className="fixed pointer-events-none z-50"
      style={{
        left: pos.left,
        top: pos.top,
        visibility: pos.ready ? 'visible' : 'hidden',
      }}>
      <div className="bg-neutral-900/95 text-white text-[11px] font-medium px-2.5 py-1.5 rounded-md shadow-xl whitespace-nowrap ring-1 ring-white/10">
        {tip.content}
      </div>
    </div>,
    document.body,
  );
}

// height is optional. When omitted, renders a simple relative wrapper so the
// parent container (CSS flex, h-full, etc.) controls the height — no internal
// scroll constraints. When provided, enables vertical scroll if naturalHeight > height.
export function ChartSVGWrap({ children, tip, svgRef, wrapRef, height, naturalHeight }) {
  if (height === undefined) {
    return (
      <div ref={wrapRef} className="w-full relative overflow-hidden">
        {children}
        <Tooltip tip={tip} svgRef={svgRef} />
      </div>
    );
  }
  const nat = naturalHeight ?? height;
  const scrollY = nat > height;
  return (
    <div ref={wrapRef} className="w-full overflow-x-hidden"
         style={{ height, overflowY: scrollY ? 'auto' : 'hidden' }}>
      <div className="relative" style={{ height: nat }}>
        {children}
        <Tooltip tip={tip} svgRef={svgRef} />
      </div>
    </div>
  );
}

// ── Vertical Bar with tooltips ───────────────────────────────
// Auto-detects when columns are too narrow → expands SVG width → horizontal scroll.
// Tooltip positioning stays correct because absolute coords are relative to the
// scrolled container (scrollLeft is naturally factored in via getBoundingClientRect).
// `onBarClick(label, i)` makes bars clickable (pointer, keyboard, the
// `clickHint` in the tooltip), as in HorizontalBarT (AI Home drill, 2026-10-23).
export function VerticalBarT({ data, color, height = 200, width: widthHint = 400, format = v => v.toLocaleString(), dataLabels, showValues = true, axisYFormat, entrance = true, ariaLabel = 'Barras verticales interactivo', onBarClick, clickHint }) {
  const componentTheme = useColorTheme('VerticalBarT');
  const clientColors = componentTheme.palette;
  if (componentTheme.override) color = componentTheme.palette[0 % componentTheme.palette.length];
  if (color === undefined) color = clientColors[0 % clientColors.length];

  const { tip, svgRef, show, hide } = useTooltip();
  // Chart-level grow cycle: first load enters from zero; a DATA
  // change re-grows every mark together (filter language, r16).
  const chartDrawn = useGrowCycle(useMemo(() => lineDataSig(data), [data]), !entrance);
  const [hoverIdx, setHoverIdx] = useState(null);
  const [wrapRef, w] = useContainerWidth(widthHint);
  // Unique per instance (audit #211): `url(#id)` resolves to the FIRST match
  // in the document, so a fixed id makes a 2nd chart borrow the 1st one's.
  const gid = useId().replace(/[^\w-]/g, '');
  if (!hasData(data)) return <div ref={wrapRef}><ChartEmpty height={height} /></div>;
  // Round axis ticks; the scale max IS the top tick (≥ data max).
  // Signed domain (audit #213): anchored at 0, extended below it when
  // there are negatives — same pattern as WaterfallChart.
  const vals = data.map(d => d.value);
  const { ticks: yTicks, max, min } = niceTicks(safeMax(vals), 5, Math.min(0, safeMin(vals)));
  const h = height, pad = { l: 45, r: 10, t: 14, b: 32 };
  const n = data.length;
  const MIN_COL_W = 32;
  const naturalW = Math.max(w, pad.l + pad.r + Math.ceil(n * MIN_COL_W / 0.65));
  const scrollX = naturalW > w;
  const cw = naturalW - pad.l - pad.r, ch = h - pad.t - pad.b;
  const py = v => pad.t + (1 - (v - min) / (max - min)) * ch;
  const bw = Math.min(cw / n * 0.65, 32);
  const rx = Math.min(6, bw / 2);
  const yAxisFmt = resolveFormatter(axisYFormat, fmtN);
  // Rule 1: data labels ON by default, bold, auto-thinned when dense.
  const labelFmt = resolveLabels(dataLabels, showValues);
  const labelIdx = labelFmt ? thinLabelIndices(data.map(d => d.value)) : null;
  return (
    <div ref={wrapRef} className="w-full relative"
         style={{ height: h, overflowX: scrollX ? 'auto' : 'hidden', overflowY: 'hidden' }}>
      <svg ref={svgRef} width={naturalW} height={h} role="img" aria-label={ariaLabel} onMouseLeave={hide}>
        {yTicks.map(v => (
          <g key={v}>
            <line x1={pad.l} x2={naturalW-pad.r} y1={py(v)} y2={py(v)} stroke={NEUTRAL.grid} strokeWidth="0.8"/>
            <text x={pad.l-5} y={py(v)+3} textAnchor="end" fontSize="11" fill={NEUTRAL.faint}>{yAxisFmt(v)}</text>
          </g>
        ))}
        {data.map((d,i) => {
          const x = pad.l+(i+0.5)*(cw/n)-bw/2, y0 = py(0), yv = py(d.value), isH = hoverIdx===i;
          const showLabel = labelFmt && (labelIdx.has(i) || isH);
          const clickable = typeof onBarClick === 'function';
          return (
            <g key={i} onMouseMove={e=>{setHoverIdx(i);show(e,`${d.label}: ${format(d.value)}${clickable&&clickHint?`\n${clickHint}`:''}`);}} onMouseLeave={()=>{setHoverIdx(null);hide();}}
              {...(clickable ? {
                role:'button', tabIndex:0, 'aria-label':`${d.label}: ${format(d.value)}`, 'data-bar-click':'',
                style:{cursor:'pointer'},
                onClick:()=>onBarClick(d.label,i),
                onKeyDown:e=>{if(e.key==='Enter'||e.key===' '){e.preventDefault();onBarClick(d.label,i);}},
              } : {})}>
              <rect x={x-4} y={pad.t} width={bw+8} height={ch+pad.b} fill="transparent"/>
              <defs><linearGradient id={`${gid}-vbt${i}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={color} stopOpacity={isH?"1":"0.9"}/><stop offset="100%" stopColor={color} stopOpacity={isH?"0.8":"0.55"}/></linearGradient></defs>
              {/* Entrance (ronda 12): the bar mark grows from the axis;
                  labels/axes stay outside the wrapper (never distorted). */}
              <EntranceGroup mode="growY" origin={`0px ${y0}px`} disabled={!entrance} drawn={chartDrawn}>
                <rect x={x} y={Math.min(y0, yv)} width={bw} height={Math.abs(yv - y0)} fill={`url(#${gid}-vbt${i})`} rx={rx} style={{filter:isH?'brightness(1.1)':'none',transition:'all 0.15s'}}/>
              </EntranceGroup>
              {showLabel&&<text x={x+bw/2} y={d.value >= 0 ? yv - 4 : yv + 12} textAnchor="middle" fontSize="11" fontWeight="700" fill={NEUTRAL.text}>{labelFmt(d.value)}</text>}
              <SlotLabel label={d.label} slot={cw / n} x={x+bw/2} y={h-pad.b+14} textAnchor="middle" fontSize="11" fill={isH?NEUTRAL.text:NEUTRAL.faint} fontWeight={isH?"600":"400"} />
            </g>
          );
        })}
      </svg>
      <Tooltip tip={tip} svgRef={svgRef} />
    </div>
  );
}

// ── Line Chart with crosshair ────────────────────────────────
export function LineChartT({ data, color, height = 200, width: widthHint = 400, showArea = true, format = v => v.toLocaleString(), dataLabels, showValues = true, axisYFormat, entrance = true, onPointClick, activePointLabel = null, clickHint, ariaLabel = 'Línea interactivo' }) {
  const componentTheme = useColorTheme('LineChartT');
  const clientColors = componentTheme.palette;
  if (componentTheme.override) color = componentTheme.palette[0 % componentTheme.palette.length];
  if (color === undefined) color = clientColors[0 % clientColors.length];

  const { tip, svgRef, show, hide } = useTooltip();
  const [hoverIdx, setHoverIdx] = useState(null);
  const [wrapRef, w] = useContainerWidth(widthHint);
  // Entrance (ronda 12): the stroke draws in; area/dots/labels fade
  // alongside. Hooks BEFORE the empty-state return (hook-order bug pin).
  const lineDraw = useLineDraw(!entrance, lineDataSig(data));
  const entered = useEntrance();
  const prm = usePRM();
  const fadeStyle = {
    opacity: (!entrance || entered) ? 1 : 0,
    transition: (!entrance || prm) ? undefined : `opacity ${PM_MOTION.durEntrance}ms ${PM_MOTION.easeOut}`,
  };
  if (!hasData(data)) return <div ref={wrapRef}><ChartEmpty height={height} /></div>;
  // Round axis ticks; domain snaps to the nice floor/ceil of the data.
  const _vals=data.map(d=>d.value);
  const { ticks: yTicks, max, min } = niceTicks(safeMax(_vals), 4, safeMin(_vals));
  // Auto-grow pad.r so the rightmost x-axis label (e.g. an ISO date)
  // doesn't clip when its anchor lands at x = w - pad.r.
  const _lastLbl = String(data[data.length-1]?.label ?? '');
  const _padR = Math.max(10, Math.ceil(_lastLbl.length * 3.9) + 4);
  const h=height,pad={l:45,r:_padR,t:14,b:28},cw=w-pad.l-pad.r,ch=h-pad.t-pad.b;
  const _r=(max-min)||1;
  const n=data.length;
  const pts=data.map((d,i)=>({x:lineX(i,n,pad.l,cw),y:pad.t+(1-(d.value-min)/_r)*ch}));
  // Rule 6: monotone-cubic smoothing — soft curve, zero overshoot;
  // the dots below mark the real data points.
  // Cross-filter por punto (2026-08-10): clic en la columna de un punto
  // setea el filtro del chart (mismo camino que los tiles/cards). El
  // punto ACTIVO se marca por FORMA, no solo color: guía vertical
  // SÓLIDA (el hover usa punteada), anillo persistente y label del eje
  // en bold — legible para daltónicos.
  const clickable = typeof onPointClick === 'function';
  const activeIdx = activePointLabel != null
    ? data.findIndex(d => String(d.label) === String(activePointLabel))
    : -1;
  const lineD=monotonePath(pts);
  const areaD=`${lineD}L${pts[n-1].x.toFixed(2)},${(pad.t+ch).toFixed(2)}L${pts[0].x.toFixed(2)},${(pad.t+ch).toFixed(2)}Z`;
  const yAxisFmt = resolveFormatter(axisYFormat, fmtN);
  // Rule 1: labels ON by default (auto-thinned); rule 2: dots on the line.
  const labelFmt = resolveLabels(dataLabels, showValues);
  const labelIdx = labelFmt ? thinLabelIndices(_vals) : null;
  return (
    <ChartSVGWrap tip={tip} svgRef={svgRef} wrapRef={wrapRef} height={h}>
      <svg ref={svgRef} width={w} height={h} role="img" aria-label={ariaLabel} onMouseLeave={()=>{setHoverIdx(null);hide();}}>
        {yTicks.map(v=>{const y=pad.t+(1-(v-min)/_r)*ch;return(
          <g key={v}><line x1={pad.l} x2={w-pad.r} y1={y} y2={y} stroke={NEUTRAL.grid} strokeWidth="0.8"/>
          <text x={pad.l-5} y={y+3} textAnchor="end" fontSize="11" fill={NEUTRAL.faint}>{yAxisFmt(v)}</text></g>
        );})}
        {showArea&&<g style={fadeStyle}><path d={areaD} fill={color} opacity="0.08"/></g>}
        <path d={lineD} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" {...lineDraw}/>
        {activeIdx>=0&&(
          <>
            <line data-active-guide="true" x1={pts[activeIdx].x} y1={pad.t} x2={pts[activeIdx].x} y2={pad.t+ch} stroke={color} strokeWidth="1.5" opacity="0.6"/>
            <circle data-active-point="true" cx={pts[activeIdx].x} cy={pts[activeIdx].y} r="6" fill="white" stroke={color} strokeWidth="3"/>
          </>
        )}
        {hoverIdx!==null&&hoverIdx!==activeIdx&&(
          <>
            <line x1={pts[hoverIdx].x} y1={pad.t} x2={pts[hoverIdx].x} y2={pad.t+ch} stroke={color} strokeWidth="1" strokeDasharray="3,3" opacity="0.4"/>
            <circle cx={pts[hoverIdx].x} cy={pts[hoverIdx].y} r="5" fill="white" stroke={color} strokeWidth="2.5"/>
          </>
        )}
        <g style={fadeStyle}>
          {pts.map((p,i)=><circle key={i} cx={p.x} cy={p.y} r={hoverIdx===i?0:lineDotRadius(n,i===0||i===n-1)} fill={color}/>)}
          {labelFmt&&pts.map((p,i)=>(labelIdx.has(i)||hoverIdx===i)&&(
            <text key={`lbl${i}`} x={p.x} y={Math.max(pad.t+10,p.y-9)} textAnchor="middle" fontSize="11" fill={color} fontWeight="700">{labelFmt(data[i].value)}</text>
          ))}
        </g>
        {data.map((d,i)=>{
          // Hit cell centred on the REAL point x. Points sit at
          // xRatio(i,n) = i/(n-1); the old i*(cw/n) band drifted left of
          // its point, up to a full cell at the right edge (2026-08-28).
          const half=n>1?(cw-2*LINE_INSET)/(n-1)/2:cw/2;return(
          <rect key={i} x={pts[i].x-half} y={pad.t} width={half*2} height={ch+pad.b} fill="transparent"
            style={clickable?{cursor:'pointer'}:undefined}
            onClick={clickable?()=>onPointClick(String(d.label), i):undefined}
            onMouseMove={e=>{setHoverIdx(i);show(e,`${d.label}: ${format(d.value)}${clickable&&clickHint?` · ${clickHint}`:''}`);}} onMouseLeave={()=>{setHoverIdx(null);hide();}}/>
        );})}
        {data.map((d,i)=>(i%xLabelStride(data.map(x=>x.label),cw,8)===0||i===activeIdx)&&(
          <text key={i} x={pts[i].x} y={h-pad.b+14} textAnchor="middle" fontSize="11" fill={i===activeIdx?NEUTRAL.text:NEUTRAL.faint} fontWeight={i===activeIdx?"700":"400"}>{d.label}</text>
        ))}
      </svg>
    </ChartSVGWrap>
  );
}

// ── Pie/Donut with hover ─────────────────────────────────────
// Redesign: donut with a central total metric is the default render
// (`donut={false}` still gives the classic pie). Segment % labels are
// painted inside slices ≥ 10% (rule 1) and the legend is a
// legend-table: swatch + name + value (bold) + share (rule 3).
export function PieChartT({ data, colors, height = 200, donut = true, width: widthHint = 360, showValues = true, entrance = true, ariaLabel }) {
  const componentTheme = useColorTheme('PieChartT');
  const clientColors = componentTheme.palette;
  if (componentTheme.override) colors = componentTheme.palette;
  if (colors === undefined) colors = clientColors;

  const { tip, svgRef, show, hide } = useTooltip();
  // Chart-level grow cycle: first load enters from zero; a DATA
  // change re-grows every mark together (filter language, r16).
  const chartDrawn = useGrowCycle(useMemo(() => lineDataSig(data), [data]), !entrance);
  const [hoverIdx, setHoverIdx] = useState(null);
  const [wrapRef, w] = useContainerWidth(widthHint);
  if (!hasData(data)) return <div ref={wrapRef}><ChartEmpty height={height} /></div>;
  const total=data.reduce((s,d)=>s+d.value,0);
  const r=Math.min(w*0.22, height*0.38);
  const fs=Math.max(9,Math.min(12,r*0.145));
  // Pie + legend centred as one block (pieCenterX, shared.jsx).
  const cx=pieCenterX(w, r, fs, data, total), cy=height/2;
  const ir=donut?r*0.585:0;
  let angle=-Math.PI/2;
  const slices=data.map((d,i)=>{
    const sweep=(total>0?(d.value/total):0)*Math.PI*2;
    const a0=angle;
    const x1o=cx+r*Math.cos(angle),y1o=cy+r*Math.sin(angle);
    const x1i=cx+ir*Math.cos(angle),y1i=cy+ir*Math.sin(angle);
    angle+=sweep;
    const x2o=cx+r*Math.cos(angle),y2o=cy+r*Math.sin(angle);
    const x2i=cx+ir*Math.cos(angle),y2i=cy+ir*Math.sin(angle);
    const large=sweep>Math.PI?1:0;
    const path=donut?`M${x1i},${y1i} L${x1o},${y1o} A${r},${r} 0 ${large} 1 ${x2o},${y2o} L${x2i},${y2i} A${ir},${ir} 0 ${large} 0 ${x1i},${y1i}`:`M${cx},${cy} L${x1o},${y1o} A${r},${r} 0 ${large} 1 ${x2o},${y2o} Z`;
    const mid=a0+sweep/2;
    const labelR=donut?(r+ir)/2:r*0.62;
    return {...d,path,color:colors[i%colors.length],pct:total>0?((d.value/total)*100).toFixed(1):'0.0',
      pctNum:total>0?(d.value/total)*100:0,
      lx:cx+labelR*Math.cos(mid),ly:cy+labelR*Math.sin(mid)};
  });
  const legX=cx+r+22, legRowH=Math.max(24,(height-40)/data.length);
  const legStartY=cy-(data.length-1)*legRowH/2;
  const h = height;
  return (
    <ChartSVGWrap tip={tip} svgRef={svgRef} wrapRef={wrapRef} height={h}>
      <svg ref={svgRef} width={w} height={h} role="img" aria-label={ariaLabel || (donut ? 'Donut interactivo' : 'Torta interactivo')} onMouseLeave={()=>{setHoverIdx(null);hide();}}>
        {/* Entrance (ronda 12): slices pop from the pie centre; labels
            and legend stay outside the wrapper. */}
        <EntranceGroup mode="pop" origin={`${cx}px ${cy}px`} disabled={!entrance} drawn={chartDrawn}>
          {slices.map((s,i)=>(
            <path key={i} d={s.path} fill={s.color} opacity={hoverIdx===null||hoverIdx===i?0.9:0.4} style={{transition:'all 0.2s'}}
              onMouseMove={e=>{setHoverIdx(i);show(e,`${s.label}: ${fmtN(s.value)} (${s.pct}%)`);}} onMouseLeave={()=>{setHoverIdx(null);hide();}}/>
          ))}
        </EntranceGroup>
        {/* Rule 1: % inside segments when the slice is ≥ 10% */}
        {showValues!==false&&slices.map((s,i)=>s.pctNum>=10&&(
          <text key={`pct${i}`} x={s.lx} y={s.ly+3.5} textAnchor="middle" fontSize={Math.max(11,fs-1)}
            fontWeight="800" fill="white" pointerEvents="none">{Math.round(s.pctNum)}%</text>
        ))}
        {donut&&<>
          <text x={cx} y={cy-2} textAnchor="middle" fontSize={r*0.27} fontWeight="800" fill={NEUTRAL.text}>{fmtN(total)}</text>
          <text x={cx} y={cy+r*0.18} textAnchor="middle" fontSize={Math.max(11,r*0.13)} fill={NEUTRAL.faint}>Total</text>
        </>}
        {slices.map((s,i)=>(
          <g key={i} transform={`translate(${legX},${legStartY+i*legRowH-5})`}
            onMouseMove={e=>{setHoverIdx(i);show(e,`${s.label}: ${fmtN(s.value)} (${s.pct}%)`);}} onMouseLeave={()=>{setHoverIdx(null);hide();}}>
            <rect width="10" height="10" rx="2.5" fill={s.color} y="1" opacity={hoverIdx===null||hoverIdx===i?1:0.4}/>
            <text x="16" y="10" fontSize={fs} fill={NEUTRAL.text} fontWeight={hoverIdx===i?"700":"500"}>{s.label}</text>
            <text x="16" y={10+fs*1.2} fontSize={fs-1} fill={NEUTRAL.muted}>
              <tspan fontWeight="700" fill={NEUTRAL.text}>{fmtN(s.value)}</tspan>
              <tspan> · {s.pct}%</tspan>
            </text>
          </g>
        ))}
      </svg>
    </ChartSVGWrap>
  );
}

// ── Multi-Line with hover crosshair ──────────────────────────
// Dual-axis mode: pass ``dualAxis: true``. Series[0] uses the left Y axis,
// series[1] uses the right Y axis. Optional ``yMin``/``yMax``/``yStep`` lock
// the left scale (and matching ``y1*`` props for the right scale); when omitted
// the scale auto-fits the data. ``ySuffix``/``y1Suffix`` append a unit on the
// tick labels (e.g. ``%`` and ``sem.``).
export function MultiLineT({
  series, labels, colors, height = 200, width: widthHint = 400,
  dataLabels, showValues = true, axisYFormat, ariaLabel = 'Multi-línea interactivo',
  dualAxis = false,
  yMin, yMax, yStep, y1Min, y1Max, y1Step,
  ySuffix = '', y1Suffix = '',
  // Opt-out for the legend delta chips (chart config `legend_deltas:
  // false`). Default TRUE keeps every existing report byte-identical;
  // reports that surface the same deltas as scorecards above the chart
  // (gating_overall, 2026-07) turn them off here so the number isn't
  // stated twice with ambiguous period semantics.
  legendDeltas = true,
  // The tooltip's "Total" row sums the series under the cursor: right for
  // parts of a whole, noise for series that are not (one price per
  // retailer). Config `tooltip_total: false`. Never shown with dualAxis.
  tooltipTotal = true,
  entrance = true,
  onPointClick, activePointLabel = null, clickHint,
  // Opt-in: clicking a legend entry hides / shows that series (the DSA
  // evolution chart). A hidden series stays in the legend, attenuated.
  legendToggle = false,
}) {
  const componentTheme = useColorTheme('MultiLineT');
  const clientColors = componentTheme.palette;
  if (componentTheme.override) colors = componentTheme.palette;
  if (colors === undefined) colors = clientColors;

  const { tip, svgRef, show, hide } = useTooltip();
  const [hoverIdx, setHoverIdx] = useState(null);
  const [hiddenSeries, setHiddenSeries] = useState(() => new Set());
  const toggleSeries = (i) => setHiddenSeries(prev => {
    const next = new Set(prev);
    if (next.has(i)) next.delete(i); else next.add(i);
    return next;
  });
  // Every series keeps its index (and so its colour); a hidden one is
  // drawn as all-gaps, which also takes it out of the axis domain.
  const allSeries = series;
  series = legendToggle && hiddenSeries.size
    ? allSeries.map((s, si) => (hiddenSeries.has(si) ? { ...s, data: s.data.map(() => null) } : s))
    : allSeries;
  const [wrapRef, w] = useContainerWidth(widthHint);
  // Entrance (ronda 12): every solid series stroke draws in (single
  // hook call, identical props spread on each path — hooks can't live
  // in the series map); dots/labels fade alongside. Hooks BEFORE the
  // empty-state return (hook-order bug pin).
  const lineDraw = useLineDraw(!entrance, lineDataSig(series));
  const entered = useEntrance();
  const prm = usePRM();
  const fadeStyle = {
    opacity: (!entrance || entered) ? 1 : 0,
    transition: (!entrance || prm) ? undefined : `opacity ${PM_MOTION.durEntrance}ms ${PM_MOTION.easeOut}`,
  };
  // Cross-filter por punto (2026-08-10): mismo contrato que LineChartT —
  // clic en la columna de un punto setea el filtro; el punto activo se
  // marca por FORMA (guía sólida vs punteada del hover, anillos
  // persistentes, label del eje en bold), no solo color.
  if (!hasData(allSeries) || !hasData(labels)) return <div ref={wrapRef}><ChartEmpty height={height} /></div>;

  const clickable = typeof onPointClick === 'function';
  const activeIdx = activePointLabel != null
    ? labels.findIndex(l => String(l) === String(activePointLabel))
    : -1;

  const dual = dualAxis && series.length >= 2;
  const leftSeries = dual ? [series[0]] : series;
  const rightSeries = dual ? series.slice(1) : [];

  const leftVals = leftSeries.flatMap(s => s.data);
  const rightVals = rightSeries.flatMap(s => s.data);
  // Axis polish 2026-07: when the scale is fully auto (no explicit
  // min/max/step props locking it), snap the domain to round ticks —
  // the tick values ARE the domain bounds. Explicit props keep the
  // historical locked-scale behavior byte-for-byte.
  const leftAuto = yMin === undefined && yMax === undefined && yStep === undefined
    ? niceTicks(safeMax(leftVals), 4, safeMin(leftVals))
    : null;
  const rightAuto = dual && rightVals.length
    && y1Min === undefined && y1Max === undefined && y1Step === undefined
    ? niceTicks(safeMax(rightVals), 4, safeMin(rightVals))
    : null;
  const leftMin = leftAuto ? leftAuto.min : (yMin ?? safeMin(leftVals) * 0.9);
  const leftMax = leftAuto ? leftAuto.max : (yMax ?? safeMax(leftVals) * 1.1);
  const rightMin = rightAuto ? rightAuto.min : (y1Min ?? (rightVals.length ? safeMin(rightVals) * 0.9 : 0));
  const rightMax = rightAuto ? rightAuto.max : (y1Max ?? (rightVals.length ? safeMax(rightVals) * 1.1 : 1));

  // Build tick lists from step when provided, otherwise default to 5 ticks
  const buildTicks = (lo, hi, step) => {
    if (typeof step === 'number' && step > 0) {
      const out = [];
      for (let v = lo; v <= hi + 1e-9; v += step) out.push(+v.toFixed(6));
      if (out[out.length - 1] < hi) out.push(hi);
      return out;
    }
    return [0, 0.25, 0.5, 0.75, 1].map(f => lo + (hi - lo) * f);
  };
  const leftTicks = leftAuto ? leftAuto.ticks : buildTicks(leftMin, leftMax, yStep);
  const rightTicks = dual
    ? (rightAuto ? rightAuto.ticks : buildTicks(rightMin, rightMax, y1Step))
    : [];

  const yAxisFmt = resolveFormatter(axisYFormat, fmtN);
  const fmtLeftTick = v => `${yAxisFmt(v)}${ySuffix}`;
  const fmtRightTick = v => `${yAxisFmt(v)}${y1Suffix ? ' ' + y1Suffix : ''}`;
  // A narrow auto scale (a share between 30 and 31 %) steps by fractions,
  // and fmtN rounds to whole numbers: the axis read "31, 31, 31, 30, 30"
  // (critique of Disponibilidad, 2026-10-27). With the default formatter
  // only, when two tick labels repeat, the TICKS carry the step's decimals.
  // Legend and tooltip keep their formatter.
  const leftStep = leftAuto ? leftAuto.step : yStep;
  const leftDecimals = leftStep > 0 && leftStep < 1
    ? Math.min(2, (String(+leftStep.toPrecision(12)).split('.')[1] || '').length)
    : 0;
  const defaultLeftFmt = resolveFormatter(axisYFormat, null) === null;
  const fmtLeftAxis = defaultLeftFmt && leftDecimals > 0
    && new Set(leftTicks.map(fmtLeftTick)).size < leftTicks.length
    ? v => `${Number(v).toLocaleString(undefined, { minimumFractionDigits: leftDecimals, maximumFractionDigits: leftDecimals })}${ySuffix}`
    : fmtLeftTick;
  const yLabelMax = Math.max(...leftTicks.map(v => fmtLeftAxis(v).length));
  const y1LabelMax = dual ? Math.max(...rightTicks.map(v => fmtRightTick(v).length)) : 0;
  const padL = Math.max(36, Math.min(80, yLabelMax * 7 + 12));
  // Non-dual: auto-grow padR by last x-axis label half-width so it
  // doesn't clip when the label index lands at n-1 (anchor at w-padR).
  const _lastXLbl = String(labels[labels.length-1] ?? '');
  const _padRSingle = Math.max(10, Math.ceil(_lastXLbl.length * 3.9) + 4);
  const padR = dual ? Math.max(36, Math.min(80, y1LabelMax * 7 + 12)) : _padRSingle;

  // Legend-table (rule 3): swatch + name + last value (bold) + delta
  // chip vs previous point. Measured first so the bottom padding can
  // reserve its height.
  const legendItems = allSeries.map((s, si) => {
    const { value, delta } = lastDelta(s.data);
    const fmtVal = dual && si >= 1 ? fmtRightTick : fmtLeftTick;
    return {
      name: s.name,
      color: s.color || colors[si % colors.length],
      shape: 'line',
      index: si,
      hidden: hiddenSeries.has(si),
      dashed: !!s.dashed,
      value: value != null ? fmtVal(value) : null,
      // `null` delta renders no chip (same as a not-computable delta).
      delta: legendDeltas ? delta : null,
    };
  });
  const lgd = legendLayout(legendItems, Math.max(120, w - 16));

  const h=height,pad={l:padL,r:padR,t:14,b:26+lgd.height},cw=w-pad.l-pad.r,ch=h-pad.t-pad.b;
  const n=labels.length;
  const px=i=>lineX(i,n,pad.l,cw);
  const pyLeft=v=>pad.t+(1-(v-leftMin)/(leftMax-leftMin||1))*ch;
  const pyRight=v=>pad.t+(1-(v-rightMin)/(rightMax-rightMin||1))*ch;
  const seriesY = (s, i) => (dual && i >= 1 ? pyRight : pyLeft)(s);
  // Rule 1: labels ON by default. Multi-series lines get extra
  // thinning — with 3+ series only extremes + peaks per series are
  // labelled regardless of length, to keep the plane readable.
  const labelFmt = resolveLabels(dataLabels, showValues);
  const labelIdxFor = (vals) =>
    series.length < 3 ? thinLabelIndices(vals) : peakLabelIndices(vals);
  const labelIdxSets = labelFmt ? series.map(s => labelIdxFor(s.data)) : null;
  // Anti-collision (axis polish 2026-07): labels sharing an x slot
  // (typically the LAST point of series that end close together) are
  // separated ≥ 12px vertically — the higher value stays up, the
  // lower one is pushed down — clamped to the plot area.
  const seriesLabels = labelFmt ? (() => {
    const byIdx = new Map();
    series.forEach((s, si) => {
      labels.forEach((_, i) => {
        if (!labelIdxSets[si].has(i)) return;
        const v = s.data[i];
        if (typeof v !== 'number' || !Number.isFinite(v)) return;
        if (!byIdx.has(i)) byIdx.set(i, []);
        byIdx.get(i).push({ si, i, v, y: seriesY(v, si) - 9 });
      });
    });
    const out = [];
    for (const group of byIdx.values()) {
      const ys = separateLabelYs(group.map(g => g.y), 12, pad.t + 10, pad.t + ch - 2);
      group.forEach((g, k) => out.push({ ...g, y: ys[k] }));
    }
    return out;
  })() : null;
  return (
    <ChartSVGWrap tip={tip} svgRef={svgRef} wrapRef={wrapRef} height={h}>
      <svg ref={svgRef} width={w} height={h} role="img" aria-label={ariaLabel} onMouseLeave={()=>{setHoverIdx(null);hide();}}>
        {leftTicks.map((v,ti)=>{
          const y=pyLeft(v);
          return (
            <g key={`yl${ti}`}>
              <line x1={pad.l} x2={w-pad.r} y1={y} y2={y} stroke={NEUTRAL.grid} strokeWidth="0.8"/>
              <text x={pad.l-5} y={y+3} textAnchor="end" fontSize="11" fill={dual?(colors[0]||NEUTRAL.faint):NEUTRAL.faint}>{fmtLeftAxis(v)}</text>
            </g>
          );
        })}
        {dual && rightTicks.map((v,ti)=>{
          const y=pyRight(v);
          return (
            <text key={`yr${ti}`} x={w-pad.r+5} y={y+3} textAnchor="start" fontSize="11" fill={colors[1]||NEUTRAL.faint}>{fmtRightTick(v)}</text>
          );
        })}
        {series.map((s,si)=>{
          // Rule 6: monotone-cubic — smooth but never overshoots.
          // A GAP is a gap. A label with no row for this series is not a
          // zero, and drawing it as one makes the line dive to the axis
          // and come back — a shape that reads as "it collapsed that
          // month". The dots and the legend delta already skipped
          // non-numbers; only the path did not, and it turned them into
          // NaN coordinates. Now the path is split into runs of real
          // points, so an absent value leaves a break.
          const runs=[]; let run=[];
          labels.forEach((_,i)=>{
            const v=s.data[i];
            if(typeof v==='number'&&Number.isFinite(v)){run.push({x:px(i),y:seriesY(v,si)});}
            else if(run.length){runs.push(run);run=[];}
          });
          if(run.length)runs.push(run);
          const d=runs.map(r=>monotonePath(r)).join(' ');
          return <path key={si} d={d} fill="none" stroke={s.color||colors[si%colors.length]} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" strokeDasharray={s.dashed?'6 4':undefined} opacity={hoverIdx!==null?0.4:0.9} {...(s.dashed?{}:lineDraw)}/>;
        })}
        {/* Rule 2: dots mark the real data points (extremes emphasized) */}
        <g style={fadeStyle}>
        {series.map((s,si)=>labels.map((_,i)=>{
          const v=s.data[i];
          if(typeof v!=='number'||!Number.isFinite(v))return null;
          return <circle key={`dot${si}-${i}`} cx={px(i)} cy={seriesY(v, si)} r={hoverIdx===i?0:lineDotRadius(n,i===0||i===n-1)} fill={s.color||colors[si%colors.length]} opacity={hoverIdx!==null?0.4:1}/>;
        }))}
        </g>
        {activeIdx>=0&&<>
          <line data-active-guide="true" x1={px(activeIdx)} y1={pad.t} x2={px(activeIdx)} y2={pad.t+ch} stroke={NEUTRAL.text} strokeWidth="1.5" opacity="0.55"/>
          {series.map((s,si)=>s.data[activeIdx]!=null&&<circle key={`a${si}`} data-active-point="true" cx={px(activeIdx)} cy={seriesY(s.data[activeIdx], si)} r="6" fill="white" stroke={colors[si%colors.length]} strokeWidth="3"/>)}
        </>}
        {hoverIdx!==null&&hoverIdx!==activeIdx&&<>
          <line x1={px(hoverIdx)} y1={pad.t} x2={px(hoverIdx)} y2={pad.t+ch} stroke={NEUTRAL.axis} strokeWidth="1" strokeDasharray="3,3"/>
          {/* A gap (null) draws no ring — never one at y=0 (audit #210). */}
          {series.map((s,si)=>{const v=s.data[hoverIdx]; return (typeof v==='number'&&Number.isFinite(v))
            ? <circle key={si} cx={px(hoverIdx)} cy={seriesY(v, si)} r="5" fill="white" stroke={colors[si%colors.length]} strokeWidth="2.5"/>
            : null;})}
        </>}
        {seriesLabels&&<g style={fadeStyle}>{seriesLabels.map(g=>(
          <text key={`lbl${g.si}-${g.i}`} x={px(g.i)} y={g.y} textAnchor="middle" fontSize="11" fill={colors[g.si%colors.length]} fontWeight="700">{labelFmt(g.v)}</text>
        ))}</g>}
        {labels.map((_,i)=>{
          // Same hit-cell alignment fix as LineChartT: centre on px(i)
          // (points at i/(n-1)), not on the i*(cw/n) band (2026-08-28).
          const half=n>1?(cw-2*LINE_INSET)/(n-1)/2:cw/2;
          const tooltipContent = (
            <MultiSeriesTooltip
              label={labels[i]}
              // Dual axis (audit #209): the series carry DIFFERENT units, so no
              // Total, and each value goes through ITS axis formatter — the same
              // one the legend uses (fmtLeftTick / fmtRightTick).
              showTotal={!dual && tooltipTotal}
              series={series.map((s, si) => {
                const v = s.data[i];
                // A gap is a gap (audit #210): '—', never fmtN(null) → "0".
                const present = typeof v === 'number' && Number.isFinite(v);
                return {
                  name: s.name,
                  value: !present ? '—' : dual ? (si >= 1 ? fmtRightTick : fmtLeftTick)(v) : v,
                  color: colors[si % colors.length],
                };
              })}
            />
          );
          return (
          <rect key={i} x={px(i)-half} y={pad.t} width={half*2} height={ch+pad.b} fill="transparent"
            style={clickable?{cursor:'pointer'}:undefined}
            onClick={clickable?()=>onPointClick(String(labels[i]), i):undefined}
            onMouseMove={e=>{setHoverIdx(i);show(e, clickable&&clickHint?(<div><div>{tooltipContent}</div><div style={{marginTop:4,fontSize:11,opacity:0.75}}>{clickHint}</div></div>):tooltipContent);}}
            onMouseLeave={()=>{setHoverIdx(null);hide();}}/>
        );})}
        {labels.map((lbl,i)=>(i%xLabelStride(labels,cw,6)===0||i===activeIdx)&&(
          <text key={i} x={px(i)} y={h-pad.b+14} textAnchor="middle" fontSize="11" fill={(hoverIdx===i||activeIdx===i)?NEUTRAL.text:NEUTRAL.faint} fontWeight={activeIdx===i?"700":hoverIdx===i?"600":"400"}>{lbl}</text>
        ))}
        <SVGLegendRows layout={lgd} width={w} top={h - lgd.height} onToggle={legendToggle ? toggleSeries : undefined} />
      </svg>
    </ChartSVGWrap>
  );
}

// ── Grouped / Stacked Bar with tooltips ──────────────────────
// stacked=false → grouped side-by-side (default)
// stacked=true  → vertically stacked; per-segment tooltip; total label above bar;
//                 legend auto-wraps in centered rows.
// Both modes: auto-scroll horizontal when columns < 32 px wide.
export function GroupedBarT({ data, keys, colors, height = 200, width: widthHint = 400, stacked = false, format = v => v.toLocaleString(), dataLabels, showValues = true, axisYFormat, entrance = true, ariaLabel }) {
  const componentTheme = useColorTheme('GroupedBarT');
  const clientColors = componentTheme.palette;
  if (componentTheme.override) colors = componentTheme.palette;
  if (colors === undefined) colors = clientColors;

  const { tip, svgRef, show, hide } = useTooltip();
  // Chart-level grow cycle: first load enters from zero; a DATA
  // change re-grows every mark together (filter language, r16).
  const chartDrawn = useGrowCycle(useMemo(() => lineDataSig(data), [data]), !entrance);
  const [hoverId, setHoverId] = useState(null); // grouped: `${i}` | stacked: `${i}-${ki}`
  const [wrapRef, w] = useContainerWidth(widthHint);
  if (!hasData(data)) return <div ref={wrapRef}><ChartEmpty height={height} /></div>;

  const n = data.length, gk = keys.length;
  const totals = data.map(d => keys.reduce((s, k) => s + (d[k] || 0), 0));
  // Round axis ticks; the scale max IS the top tick (≥ data max).
  // Signed domain for grouped bars (audit #213): extended below 0 when a
  // value is negative. The stacked branch keeps min=0 (unchanged).
  const flat = data.flatMap(d => keys.map(k => d[k] || 0));
  const { ticks: yTicks, max, min } = niceTicks(
    stacked ? safeMax(totals) : safeMax(flat), 5,
    stacked ? 0 : Math.min(0, safeMin(flat)),
  );

  const yAxisFmt = resolveFormatter(axisYFormat, fmtN);
  // Rule 1: labels ON by default (thinned); rule 3: legend-table.
  const labelFmt = resolveLabels(dataLabels, showValues);
  const totalLabelIdx = labelFmt ? thinLabelIndices(totals) : null;

  // Legend-table: swatch + key + last value (bold) + delta chip vs
  // the previous row of the same key.
  const legendItems = keys.map((k, ki) => {
    const { value, delta } = lastDelta(data.map(d => d[k]));
    return {
      name: k,
      color: colors[ki % colors.length],
      value: value != null ? yAxisFmt(value) : null,
      delta,
    };
  });

  // Auto-scroll horizontal
  const MIN_COL_W = stacked ? 24 : 32;
  const naturalW = Math.max(w, 48 + 12 + Math.ceil(n * MIN_COL_W / 0.65));
  const lgd = legendLayout(legendItems, Math.max(120, naturalW - 16));
  const LEGEND_H = lgd.height + 6;
  const pad = { l: 48, r: 12, t: 14, b: 36 + LEGEND_H };
  const scrollX = naturalW > w;
  const cw = naturalW - pad.l - pad.r, ch = height - pad.t - pad.b;
  const py = v => pad.t + (1 - (v - min) / (max - min)) * ch;
  const h = height;

  const gap = 2;
  const groupW = stacked
    ? Math.min(cw / n * 0.6, 40)
    : Math.min(cw / n * 0.65, 22 * gk + gap * (gk - 1));
  const bw = stacked ? groupW : (groupW - gap * (gk - 1)) / gk;
  const barRx = Math.min(6, Math.max(2, bw / 2));

  return (
    <div ref={wrapRef} className="w-full relative"
         style={{ height: h, overflowX: scrollX ? 'auto' : 'hidden', overflowY: 'hidden' }}>
      <svg ref={svgRef} width={naturalW} height={h} role="img"
           aria-label={ariaLabel ?? (stacked ? 'Barras apiladas interactivo' : 'Barras agrupadas interactivo')}
           onMouseLeave={() => { setHoverId(null); hide(); }}>
        {/* Grid — round ticks */}
        {yTicks.map(v => (
          <g key={v}>
            <line x1={pad.l} x2={naturalW-pad.r} y1={py(v)} y2={py(v)} stroke={NEUTRAL.grid} strokeWidth="0.8"/>
            <text x={pad.l-6} y={py(v)+4} textAnchor="end" fontSize="11" fill={NEUTRAL.faint}>{yAxisFmt(v)}</text>
          </g>
        ))}

        {/* Bars */}
        {data.map((d, i) => {
          const gx = pad.l + (i + 0.5) * (cw / n) - groupW / 2;

          if (stacked) {
            let yBottom = pad.t + ch;
            const total = totals[i];
            const totalBarH = total / max * ch;
            const isGroupH = typeof hoverId === 'string' && hoverId.startsWith(`${i}-`);
            return (
              <g key={i}>
                {keys.map((k, ki) => {
                  const segH = Math.max(0, (d[k] || 0) / max * ch);
                  const y = yBottom - segH;
                  yBottom -= segH;
                  const hId = `${i}-${ki}`;
                  const isH = hoverId === hId;
                  const share = total > 0 ? (d[k] || 0) / total : 0;
                  return (
                    <g key={k}>
                      {/* Entrance (ronda 12): each segment grows from the
                          axis baseline — the whole column rises together. */}
                      <EntranceGroup mode="growY" origin={`0px ${pad.t + ch}px`} disabled={!entrance} drawn={chartDrawn}>
                      <rect x={gx} y={y} width={groupW} height={segH}
                        fill={colors[ki % colors.length]} rx={ki === gk - 1 ? 3 : 0}
                        opacity={isH ? 1 : isGroupH ? 0.9 : 0.82}
                        style={{ transition: 'opacity 0.15s' }}
                        onMouseMove={e => { setHoverId(hId); show(e, (
                          <MultiSeriesTooltip
                            label={d.label}
                            valueFmt={format}
                            series={keys.map((kk, kki) => ({
                              name: kk,
                              value: d[kk] || 0,
                              color: colors[kki % colors.length],
                            }))}
                          />
                        )); }}
                        onMouseLeave={() => { setHoverId(null); hide(); }}/>
                      </EntranceGroup>
                      {/* Rule 1: % inside segments ≥ 10% of the column */}
                      {labelFmt && share >= 0.1 && segH >= 12 && groupW >= 22 && (
                        <text x={gx+groupW/2} y={y+segH/2+3.5} textAnchor="middle" fontSize="11"
                          fontWeight="800" fill="white" pointerEvents="none">{Math.round(share*100)}%</text>
                      )}
                    </g>
                  );
                })}
                {totalBarH > 0 && labelFmt && (totalLabelIdx.has(i) || isGroupH) && (
                  <text x={gx+groupW/2} y={pad.t+ch-totalBarH-4} textAnchor="middle" fontSize="11"
                    fontWeight="700" fill={NEUTRAL.text}>{labelFmt(total)}</text>
                )}
                <SlotLabel label={d.label} slot={cw / n} x={gx+groupW/2} y={h-pad.b+16} textAnchor="middle" fontSize="11"
                  fill={isGroupH ? NEUTRAL.text : NEUTRAL.faint}
                  fontWeight={isGroupH ? "600" : "400"} />
              </g>
            );
          } else {
            // Grouped mode — per-bar tooltip
            const isGroupH = typeof hoverId === 'string' && hoverId.startsWith(`${i}-`);
            return (
              <g key={i}>
                {keys.map((k, ki) => {
                  const v = d[k] || 0, y0 = py(0), yv = py(v);
                  const hId = `${i}-${ki}`;
                  const isH = hoverId === hId;
                  return (
                    <g key={k}>
                      {/* Entrance (ronda 12): bars grow from the axis. */}
                      <EntranceGroup mode="growY" origin={`0px ${y0}px`} disabled={!entrance} drawn={chartDrawn}>
                      <rect x={gx+ki*(bw+gap)} y={Math.min(y0, yv)} width={bw} height={Math.abs(yv - y0)}
                        fill={colors[ki % colors.length]} rx={barRx}
                        opacity={isH ? 1 : isGroupH ? 0.85 : 0.8} style={{ transition: 'opacity 0.15s' }}
                        onMouseMove={e => { setHoverId(hId); show(e, `${d.label} — ${k}: ${format(d[k]||0)}`); }}
                        onMouseLeave={() => { setHoverId(null); hide(); }}/>
                      </EntranceGroup>
                      {/* Rule 1: per-bar value labels when bars are wide
                          enough to carry them without overlap */}
                      {labelFmt && bw >= 16 && (totalLabelIdx.has(i) || isH) && (
                        <text x={gx+ki*(bw+gap)+bw/2} y={v >= 0 ? yv - 3 : yv + 10} textAnchor="middle" fontSize="11"
                          fontWeight="700" fill={NEUTRAL.text}>{labelFmt(d[k] || 0)}</text>
                      )}
                    </g>
                  );
                })}
                <SlotLabel label={d.label} slot={cw / n} x={gx+groupW/2} y={h-pad.b+16} textAnchor="middle" fontSize="11"
                  fill={isGroupH ? NEUTRAL.text : NEUTRAL.faint}
                  fontWeight={isGroupH ? "600" : "400"} />
              </g>
            );
          }
        })}

        {/* Legend-table (rule 3): swatch + key + last value + delta chip */}
        <SVGLegendRows layout={lgd} width={naturalW} top={h - lgd.height - 2} />
      </svg>
      <Tooltip tip={tip} svgRef={svgRef} />
    </div>
  );
}

// ── Combo Chart with tooltips (bar + line) ────────────────────
export function ComboChartT({ data, barKey='bar', lineKey='line', barLabel='Barras', lineLabel='Línea', barColor, lineColor, height=220, width: widthHint=400, format=v=>v.toLocaleString(), dataLabels, showValues = true, axisYFormat, lineSuffix='', entrance = true, ariaLabel='Barras + línea interactivo' }) {
  const componentTheme = useColorTheme('ComboChartT');
  const clientColors = componentTheme.palette;
  if (componentTheme.override) barColor = componentTheme.palette[0 % componentTheme.palette.length];
  if (barColor === undefined) barColor = clientColors[0 % clientColors.length];
  if (componentTheme.override) lineColor = componentTheme.palette[2 % componentTheme.palette.length];
  if (lineColor === undefined) lineColor = clientColors[2 % clientColors.length];

  const { tip, svgRef, show, hide } = useTooltip();
  // Chart-level grow cycle: first load enters from zero; a DATA
  // change re-grows every mark together (filter language, r16).
  const chartDrawn = useGrowCycle(useMemo(() => lineDataSig(data), [data]), !entrance);
  const [hoverIdx, setHoverIdx] = useState(null);
  const [wrapRef, w] = useContainerWidth(widthHint);
  // Entrance (ronda 12): bars grow from the axis (EntranceGroup at the
  // marks), the line draws in, its dots/labels fade alongside. Hooks
  // BEFORE the empty-state return (hook-order bug pin).
  const lineDraw = useLineDraw(!entrance, lineDataSig(data));
  const entered = useEntrance();
  const prm = usePRM();
  const fadeStyle = {
    opacity: (!entrance || entered) ? 1 : 0,
    transition: (!entrance || prm) ? undefined : `opacity ${PM_MOTION.durEntrance}ms ${PM_MOTION.easeOut}`,
  };
  // Unique per instance (audit #211): `url(#id)` resolves to the FIRST match
  // in the document, so a fixed id makes a 2nd chart borrow the 1st one's.
  const gid = useId().replace(/[^\w-]/g, '');
  if (!hasData(data)) return <div ref={wrapRef}><ChartEmpty height={height} /></div>;
  const barVals=data.map(d=>d[barKey]||0), lineVals=data.map(d=>d[lineKey]||0);
  // Round ticks on the bar axis; the line keeps its own tight scale
  // (its right-side labels render at the shared gridlines, rounded).
  const { ticks: yTicks, max: barMax } = niceTicks(safeMax(barVals));
  // Pad AWAY from the data on each side (audit #212): `min*0.92` pulled a
  // NEGATIVE minimum toward 0 and the lowest point fell under the baseline.
  // Positive-only data keeps the exact old domain; all-zero keeps 0..1.08.
  const lineLo=safeMin(lineVals);
  const lineHi=lineLo<0 ? lineVals.reduce((m,v)=>(Number.isFinite(v)&&v>m?v:m), lineLo) : safeMax(lineVals);
  const lineMax=lineHi>0 ? lineHi*1.08 : lineHi*0.92, lineMin=lineLo<0 ? lineLo*1.08 : lineLo*0.92;
  const yAxisFmt = resolveFormatter(axisYFormat, fmtN);
  // Rule 1: labels ON by default (thinned); rule 3: legend-table.
  const labelFmt = resolveLabels(dataLabels, showValues);
  const labelIdx = labelFmt ? thinLabelIndices(barVals) : null;
  const fmtLineVal = v => `${yAxisFmt(v)}${lineSuffix}`;
  const barLast = lastDelta(barVals);
  const lineLast = lastDelta(lineVals);
  const legendItems = [
    { name: barLabel, color: barColor, value: barLast.value != null ? yAxisFmt(barLast.value) : null, delta: barLast.delta },
    { name: lineLabel, color: lineColor, shape: 'line', value: lineLast.value != null ? fmtLineVal(lineLast.value) : null, delta: lineLast.delta },
  ];
  const lgd = legendLayout(legendItems, Math.max(120, w - 16));
  const h=height,pad={l:50,r:50,t:14,b:26+lgd.height},cw=w-pad.l-pad.r,ch=h-pad.t-pad.b,n=data.length;
  const bw=Math.min(cw/n*0.5,30);
  const barRx=Math.min(6,Math.max(2,bw/2));
  // Band scale, matching the bars: the dot must sit on ITS bar's center
  // (edge-to-edge xRatio drifted right of each bar as i grew).
  const lx=i=>pad.l+(i+0.5)*(cw/n);
  const ly=v=>pad.t+(1-(v-lineMin)/(lineMax-lineMin))*ch;
  // Rule 6: monotone-cubic for the line half — no overshoot.
  const lineD=monotonePath(data.map((d,i)=>({x:lx(i),y:ly(d[lineKey]||0)})));
  return (
    <ChartSVGWrap tip={tip} svgRef={svgRef} wrapRef={wrapRef} height={h}>
      <svg ref={svgRef} width={w} height={h} role="img" aria-label={ariaLabel} onMouseLeave={()=>{setHoverIdx(null);hide();}}>
        {yTicks.map(v=>{const f=barMax>0?v/barMax:0;return(
          <g key={v}>
            <line x1={pad.l} x2={w-pad.r} y1={pad.t+ch*(1-f)} y2={pad.t+ch*(1-f)} stroke={NEUTRAL.grid} strokeWidth="0.8"/>
            <text x={pad.l-6} y={pad.t+ch*(1-f)+4} textAnchor="end" fontSize="11" fill={NEUTRAL.faint}>{yAxisFmt(v)}</text>
            <text x={w-pad.r+6} y={pad.t+ch*(1-f)+4} fontSize="11" fill={lineColor} opacity="0.65">{Math.round(lineMin+(lineMax-lineMin)*f)}{lineSuffix}</text>
          </g>
        );})}
        {(() => {
          // Thin X-axis labels so they don't overlap: ~6 visible at most,
          // fewer when long labels (ISO dates) need more room than the
          // plot gives them (``xLabelStride``).
          const labelStride = xLabelStride(data.map(d => d.label), cw, 6);
          return data.map((d, i) => {
            const bh = (d[barKey] || 0) / barMax * ch;
            const x = pad.l + (i + 0.5) * (cw / n) - bw / 2;
            const isH = hoverIdx === i;
            const showLabel = isH || i % labelStride === 0;
            return (
              <g key={i}
                onMouseMove={e=>{setHoverIdx(i);show(e, (
                  <MultiSeriesTooltip
                    label={d.label}
                    showTotal={false}
                    valueFmt={format}
                    series={[
                      { name: barLabel, value: d[barKey] || 0, color: barColor },
                      {
                        name: lineLabel,
                        value: lineSuffix
                          ? `${(d[lineKey] || 0)}${lineSuffix}`
                          : (d[lineKey] || 0),
                        color: lineColor,
                      },
                    ]}
                  />
                ));}}
                onMouseLeave={()=>{setHoverIdx(null);hide();}}>
                <rect x={x-6} y={pad.t} width={bw+12} height={ch+pad.b} fill="transparent"/>
                <defs><linearGradient id={`${gid}-cct${i}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={barColor} stopOpacity={isH?"1":"0.9"}/><stop offset="100%" stopColor={barColor} stopOpacity={isH?"0.6":"0.45"}/></linearGradient></defs>
                <EntranceGroup mode="growY" origin={`0px ${pad.t + ch}px`} disabled={!entrance} drawn={chartDrawn}>
                  <rect x={x} y={pad.t+ch-bh} width={bw} height={bh} fill={`url(#${gid}-cct${i})`} rx={barRx} style={{filter:isH?'brightness(1.1)':'none',transition:'all 0.15s'}}/>
                </EntranceGroup>
                {labelFmt&&(labelIdx.has(i)||isH)&&<text x={x+bw/2} y={pad.t+ch-bh-4} textAnchor="middle" fontSize="11" fontWeight="700" fill={NEUTRAL.text}>{labelFmt(d[barKey]||0)}</text>}
                {showLabel && (
                  <text x={x+bw/2} y={h-pad.b+16} textAnchor="middle" fontSize="11" fill={isH?NEUTRAL.text:NEUTRAL.faint} fontWeight={isH?"600":"400"}>{d.label}</text>
                )}
              </g>
            );
          });
        })()}
        <path d={lineD} fill="none" stroke={lineColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" {...lineDraw}/>
        {hoverIdx!==null&&<>
          <line x1={lx(hoverIdx)} y1={pad.t} x2={lx(hoverIdx)} y2={pad.t+ch} stroke={lineColor} strokeWidth="1" strokeDasharray="3,3" opacity="0.4"/>
          <circle cx={lx(hoverIdx)} cy={ly(data[hoverIdx][lineKey]||0)} r="5" fill="white" stroke={lineColor} strokeWidth="2.5"/>
        </>}
        {/* Rule 2: solid density-adaptive dots (extremes emphasized) */}
        <g style={fadeStyle}>
          {data.map((d,i)=><circle key={i} cx={lx(i)} cy={ly(d[lineKey]||0)} r={hoverIdx===i?0:lineDotRadius(n,i===0||i===n-1)} fill={lineColor}/>)}
          {/* Rule 1: line values at the extremes, in the line color */}
          {labelFmt&&[0,n-1].filter((v,ix,arr)=>arr.indexOf(v)===ix).map(i=>(
            <text key={`ll${i}`} x={lx(i)} y={Math.max(pad.t+10,ly(data[i][lineKey]||0)-9)} textAnchor={i===0?'start':'end'} fontSize="11" fontWeight="700" fill={lineColor}>{fmtLineVal(data[i][lineKey]||0)}</text>
          ))}
        </g>
        {/* Legend-table (rule 3): swatch + name + last value + delta */}
        <SVGLegendRows layout={lgd} width={w} top={h - lgd.height} />
      </svg>
    </ChartSVGWrap>
  );
}

// ── Horizontal Bar with tooltips ─────────────────────────────
// Auto-detects when rows are too thin → expands SVG height → vertical scroll via
// ChartSVGWrap(naturalHeight). Tooltip absolute-positioning stays correct because
// the inner relative div grows to naturalHeight (see ChartSVGWrap).
// ── Scatter (interactive) ────────────────────────────────────
// X / Y axis labels at 0 / .5 / 1 fractions plus a hover tooltip per
// point. `data` shape: `{ x, y, label?, color? }[]`. The static variant
// `ScatterChart` is still exported for cases where hover would be
// noise (e.g. dense PDFs). The interactive one is the default render
// from `GenericChart` so analysts can probe individual points.
export function ScatterChartT({
  data,
  color,
  height = 220,
  width: widthHint = 400,
  // Per-axis formatters. Falls through to `format` when individual
  // hooks aren't set, then to `fmtN` (compact magnitudes like 1.2M).
  // Tooltip shows label · x · y, each formatted independently.
  format,
  xFormat,
  yFormat,
  xAxisLabel,
  yAxisLabel,
  dataLabels,
  showValues = true,
  axisXFormat,
  axisYFormat,
  entrance = true,
  ariaLabel = 'Dispersión interactivo',
}) {
  const componentTheme = useColorTheme('ScatterChartT');
  const clientColors = componentTheme.palette;
  if (componentTheme.override) color = componentTheme.palette[0 % componentTheme.palette.length];
  if (color === undefined) color = clientColors[0 % clientColors.length];

  const { tip, svgRef, show, hide } = useTooltip();
  // Chart-level grow cycle: first load enters from zero; a DATA
  // change re-grows every mark together (filter language, r16).
  const chartDrawn = useGrowCycle(useMemo(() => lineDataSig(data), [data]), !entrance);
  const [hoverIdx, setHoverIdx] = useState(null);
  const [wrapRef, w] = useContainerWidth(widthHint);
  if (!hasData(data)) return <div ref={wrapRef}><ChartEmpty height={height} /></div>;
  const xFmt = resolveFormatter(axisXFormat, xFormat || format || fmtN);
  const yFmt = resolveFormatter(axisYFormat, yFormat || format || fmtN);
  // Rule 1 with scatter-specific thinning: points are unordered, so
  // "extremes + peaks" has no meaning — default labels only render
  // when the cloud is small (n ≤ 12). An explicit editor spec
  // (`dataLabels.show: true`) labels every point.
  const _lblFmt = resolveLabels(dataLabels, showValues, yFmt);
  const labelFmt = _lblFmt && (dataLabels?.show === true || data.length <= 12) ? _lblFmt : null;
  // Round ticks on both axes; the domain max IS the top tick.
  const { ticks: xTicks, max: xMax } = niceTicks(safeMax(data.map(d => d.x)));
  const { ticks: yTicks, max: yMax } = niceTicks(safeMax(data.map(d => d.y)));
  // Auto-grow left padding so the longest Y label clears the chart
  // area. `7` is a good ballpark for fontSize 9 character width; min
  // padding of 44 keeps the small-label case clean.
  const yLabelMax = Math.max(...yTicks.map(v => yFmt(v).length));
  const padL = Math.max(44, Math.min(96, yLabelMax * 7 + 12));
  const h = height, pad = { l: padL, r: 16, t: 14, b: 32 };
  const cw = w - pad.l - pad.r, ch = h - pad.t - pad.b;
  // Inset the data area so points at x=0 / y=0 don't sit flush against
  // the axes — gives a visual margin between marks and axis labels.
  const AXIS_INSET_X = 12, AXIS_INSET_Y = 12;
  const dataW = Math.max(0, cw - AXIS_INSET_X);
  const dataH = Math.max(0, ch - AXIS_INSET_Y);
  const xToCx = (x) => pad.l + AXIS_INSET_X + (x / xMax) * dataW;
  const yToCy = (y) => pad.t + (1 - y / yMax) * dataH;
  return (
    <ChartSVGWrap tip={tip} svgRef={svgRef} wrapRef={wrapRef}>
      <svg ref={svgRef} width={w} height={h} role="img" aria-label={ariaLabel} onMouseLeave={() => { setHoverIdx(null); hide(); }}>
        {yTicks.map(v => (
          <g key={v}>
            <line x1={pad.l} x2={w - pad.r} y1={yToCy(v)} y2={yToCy(v)} stroke={NEUTRAL.grid} strokeWidth="0.8" />
            <text x={pad.l - 6} y={yToCy(v) + 4} textAnchor="end" fontSize="11" fill={NEUTRAL.faint}>{yFmt(v)}</text>
          </g>
        ))}
        {xTicks.map(v => (
          <text key={v} x={xToCx(v)} y={h - pad.b + 16} textAnchor="middle" fontSize="11" fill={NEUTRAL.faint}>{xFmt(v)}</text>
        ))}
        {/* Entrance (ronda 12): fade — scaling would lie about the
            points' positions (density marks). */}
        <EntranceGroup mode="fade" disabled={!entrance} drawn={chartDrawn}>
        {data.map((d, i) => {
          const cx = xToCx(d.x);
          const cy = yToCy(d.y);
          const isH = hoverIdx === i;
          const r = (d.size || 5) * (isH ? 1.4 : 1);
          return (
            <g key={i}>
              <circle
                cx={cx}
                cy={cy}
                r={r}
                fill={d.color || color}
                opacity={isH ? 0.95 : 0.6}
                stroke={isH ? NEUTRAL.text : 'transparent'}
                strokeWidth={isH ? 1.2 : 0}
                style={{ transition: 'all 0.15s', cursor: 'pointer' }}
                onMouseMove={e => {
                  setHoverIdx(i);
                  const lbl = d.label != null ? `${d.label} — ` : '';
                  const xL = xAxisLabel || 'x';
                  const yL = yAxisLabel || 'y';
                  show(e, `${lbl}${xL}: ${xFmt(d.x)}  ·  ${yL}: ${yFmt(d.y)}`);
                }}
                onMouseLeave={() => { setHoverIdx(null); hide(); }}
              />
              {labelFmt && (
                <text x={cx} y={cy - r - 4} textAnchor="middle" fontSize="11" fill={NEUTRAL.text} fontWeight="700">{labelFmt(d.y)}</text>
              )}
            </g>
          );
        })}
        </EntranceGroup>
      </svg>
    </ChartSVGWrap>
  );
}

// ── Bubble (interactive) ─────────────────────────────────────
// Same axes layout as ScatterT but radius is driven by `size`. Each
// axis has its own formatter so x can be percent while size stays
// raw count, etc. `pad.l` auto-grows with the longest Y label so
// bubbles never overlap the axis numbers.
export function BubbleChartT({
  data,
  colors,
  height = 240,
  width: widthHint = 400,
  format,
  xFormat,
  yFormat,
  sizeFormat,
  xAxisLabel,
  yAxisLabel,
  sizeAxisLabel,
  dataLabels,
  showValues = true,
  axisXFormat,
  axisYFormat,
  entrance = true,
  ariaLabel = 'Burbuja interactivo',
}) {
  const componentTheme = useColorTheme('BubbleChartT');
  const clientColors = componentTheme.palette;
  if (componentTheme.override) colors = componentTheme.palette;
  if (colors === undefined) colors = clientColors;

  const { tip, svgRef, show, hide } = useTooltip();
  // Chart-level grow cycle: first load enters from zero; a DATA
  // change re-grows every mark together (filter language, r16).
  const chartDrawn = useGrowCycle(useMemo(() => lineDataSig(data), [data]), !entrance);
  const [hoverIdx, setHoverIdx] = useState(null);
  const [wrapRef, w] = useContainerWidth(widthHint);
  if (!hasData(data)) return <div ref={wrapRef}><ChartEmpty height={height} /></div>;
  const xFmt = resolveFormatter(axisXFormat, xFormat || format || fmtN);
  const yFmt = resolveFormatter(axisYFormat, yFormat || format || fmtN);
  // Size never makes sense as a percent — default to fmtN (compact).
  const sFmt = sizeFormat || fmtN;
  // Rule 1 with bubble-specific thinning: same criterion as scatter —
  // default labels only for small clouds (n ≤ 12); an explicit editor
  // spec labels everything.
  const _lblFmt = resolveLabels(dataLabels, showValues, sFmt);
  const labelFmt = _lblFmt && (dataLabels?.show === true || data.length <= 12) ? _lblFmt : null;
  // Round ticks on both axes; the domain max IS the top tick.
  const { ticks: xTicks, max: xMax } = niceTicks(safeMax(data.map(d => d.x)));
  const { ticks: yTicks, max: yMax } = niceTicks(safeMax(data.map(d => d.y)));
  const sMax = safeMax(data.map(d => d.size));
  // Auto-grow padding so largest bubble + label both clear the axis.
  // Reserve at least the radius of the biggest bubble at f=0 (x=0)
  // so it doesn't crash into the Y axis labels.
  const yLabelMax = Math.max(...yTicks.map(v => yFmt(v).length));
  const padL = Math.max(56, Math.min(110, yLabelMax * 7 + 28));
  const h = height, pad = { l: padL, r: 24, t: 14, b: 32 };
  const cw = w - pad.l - pad.r, ch = h - pad.t - pad.b;
  // Inset for bubbles must clear the largest possible radius so a
  // bubble at the corner still has visual margin from the axis.
  const MAX_R = 23; // 5 base + 18 size factor
  const AXIS_INSET_X = MAX_R + 6, AXIS_INSET_Y = MAX_R + 6;
  const dataW = Math.max(0, cw - AXIS_INSET_X);
  const dataH = Math.max(0, ch - AXIS_INSET_Y);
  const xToCx = (x) => pad.l + AXIS_INSET_X + (x / xMax) * dataW;
  const yToCy = (y) => pad.t + (1 - y / yMax) * dataH;
  return (
    <ChartSVGWrap tip={tip} svgRef={svgRef} wrapRef={wrapRef}>
      <svg ref={svgRef} width={w} height={h} role="img" aria-label={ariaLabel} onMouseLeave={() => { setHoverIdx(null); hide(); }}>
        {yTicks.map(v => (
          <g key={v}>
            <line x1={pad.l} x2={w - pad.r} y1={yToCy(v)} y2={yToCy(v)} stroke={NEUTRAL.grid} strokeWidth="0.8" />
            <text x={pad.l - 6} y={yToCy(v) + 4} textAnchor="end" fontSize="11" fill={NEUTRAL.faint}>{yFmt(v)}</text>
          </g>
        ))}
        {xTicks.map(v => (
          <text key={v} x={xToCx(v)} y={h - pad.b + 16} textAnchor="middle" fontSize="11" fill={NEUTRAL.faint}>{xFmt(v)}</text>
        ))}
        {/* Entrance (ronda 12): fade — density marks, no scaling. */}
        <EntranceGroup mode="fade" disabled={!entrance} drawn={chartDrawn}>
        {data.map((d, i) => {
          const cx = xToCx(d.x);
          const cy = yToCy(d.y);
          const r = 5 + (d.size / sMax) * 18;
          const isH = hoverIdx === i;
          const col = colors[i % colors.length];
          return (
            <g key={i}>
              <circle
                cx={cx}
                cy={cy}
                r={r}
                fill={col}
                opacity={isH ? 0.85 : 0.55}
                stroke={col}
                strokeWidth={isH ? 2 : 1}
                style={{ transition: 'all 0.15s', cursor: 'pointer' }}
                onMouseMove={e => {
                  setHoverIdx(i);
                  const lbl = d.label != null ? `${d.label} — ` : '';
                  const xL = xAxisLabel || 'x';
                  const yL = yAxisLabel || 'y';
                  const sL = sizeAxisLabel || 'size';
                  show(
                    e,
                    `${lbl}${xL}: ${xFmt(d.x)}  ·  ${yL}: ${yFmt(d.y)}  ·  ${sL}: ${sFmt(d.size)}`,
                  );
                }}
                onMouseLeave={() => { setHoverIdx(null); hide(); }}
              />
              {labelFmt && (
                <text x={cx} y={cy - r - 4} textAnchor="middle" fontSize="11" fill={NEUTRAL.text} fontWeight="700">{labelFmt(d.size ?? d.y)}</text>
              )}
            </g>
          );
        })}
        </EntranceGroup>
      </svg>
    </ChartSVGWrap>
  );
}

// Redesign rule 5 — "ranked style" (Falabella-capture pattern):
// numbered rank chip + row label + pill-shaped track with a tint
// degradé of the base color per rank + bold value + share-of-total %.
// Rank / tint follow row order (callers feed top-N queries already
// sorted); the component never re-sorts user data.
// Optional per-row `color` on a data item paints THAT bar (authored
// buckets, e.g. the coverage ring's green → red tramos) instead of the
// rank degradé. `onBarClick(label, i)` makes rows clickable (pointer,
// Enter/Space, `clickHint` appended to the tooltip); absent = read-only.
export function HorizontalBarT({ data, color, height = 200, width: widthHint = 400, format = v => v.toLocaleString(), dataLabels, showValues = true, showShare = true, density = 'comfortable', entrance = true, ariaLabel = 'Barras horizontales interactivo', onBarClick, clickHint }) {
  const componentTheme = useColorTheme('HorizontalBarT');
  const clientColors = componentTheme.palette;
  if (componentTheme.override) color = componentTheme.palette[1 % componentTheme.palette.length];
  if (color === undefined) color = clientColors[1 % clientColors.length];

  const { tip, svgRef, show, hide } = useTooltip();
  // Chart-level grow cycle: first load enters from zero; a DATA
  // change re-grows every mark together (filter language, r16).
  const chartDrawn = useGrowCycle(useMemo(() => lineDataSig(data), [data]), !entrance);
  const [hoverIdx, setHoverIdx] = useState(null);
  const [wrapRef, w] = useContainerWidth(widthHint);
  if (!hasData(data)) return <div ref={wrapRef}><ChartEmpty height={height} /></div>;
  const max=safeMax(data.map(d=>d.value));
  const total=data.reduce((s,d)=>s+(typeof d.value==='number'&&Number.isFinite(d.value)?d.value:0),0);
  const D = barDensity(density);
  const CHIP_W = D.chipW, CHIP_GAP = D.chipGap, LABEL_GAP = 8;
  // Adaptive label channel (2026-07-14 feedback): grows with the
  // longest label up to ~38% of the chart width; labels that still
  // don't fit wrap to two <tspan> lines (see barLabelLayout). En
  // compacto el chip no existe, así que sus 28px vuelven al canal.
  const labelMaxPx = Math.max(64, Math.round(w * 0.38) - CHIP_W - CHIP_GAP - LABEL_GAP);
  const { rows: labelRows, textW: labelW } = barLabelLayout(data.map(d => d.label), labelMaxPx, 62, D.rowH >= 24);
  const padL = CHIP_W + CHIP_GAP + labelW + LABEL_GAP;
  // Right column: bold value + muted share %. Hidden labels shrink it.
  const labelFmt = resolveLabels(dataLabels, showValues, format);
  const n=data.length;
  const naturalH = Math.max(height, 16 + n * D.rowH); // 16 = pad.t + pad.b
  const scrollY = naturalH > height;
  // Right column auto-grow (redesign feedback r2): measure the widest
  // formatted value + share % and reserve exactly that much, plus a
  // scrollbar allowance when the chart scrolls vertically — a classic
  // (non-overlay) scrollbar overlays ~17px of the SVG's right edge,
  // which used to clip the last characters of the value / %.
  const SB_W = scrollY ? 17 : 0;
  const EDGE = 6, VAL_GAP = 8, TRACK_GAP = 8;
  // Share-of-total: solo cuando los valores SON partes de un todo
  // (conteos, montos). Un indice (mercado = 100), un precio promedio,
  // un ratio o un puntaje no son aditivos, asi que el % del total no
  // corresponde a ninguna cantidad real — el "6.4%" que acompanaba al
  // indice 72 de HISENSE no era nada. Sigue ON por defecto: los
  // rankeados de conteos que ya existen lo esperan.
  // Ojo: la MISMA geometria vive dos veces (ver shared.jsx) — este
  // cambio tiene su espejo en `HorizontalBar` de charts.jsx.
  const shareTxts = showShare
    ? data.map(d => (total > 0 && Number.isFinite(d.value) ? `${((d.value / total) * 100).toFixed(1)}%` : null))
    : data.map(() => null);
  const valueW = labelFmt ? Math.max(...data.map(d => String(labelFmt(d.value)).length)) * D.valueCharPx : 0;
  const shareW = labelFmt && shareTxts.some(s => s != null)
    ? Math.max(...shareTxts.filter(s => s != null).map(s => s.length)) * D.shareCharPx : 0;
  const padR = labelFmt
    ? Math.ceil(TRACK_GAP + valueW + (shareW ? VAL_GAP + shareW : 0) + EDGE) + SB_W
    : 16 + SB_W;
  const shareX = w - SB_W - EDGE;                             // right edge of the share %
  const valueX = shareW ? shareX - shareW - VAL_GAP : shareX; // right edge of the bold value
  const pad={l:padL,r:padR,t:8,b:8};
  const cw=w-pad.l-pad.r, ch=naturalH-pad.t-pad.b;
  const bh=Math.min(ch/n*0.6,D.barMax);
  const rx=Math.min(9,bh/2);
  return (
    <ChartSVGWrap tip={tip} svgRef={svgRef} wrapRef={wrapRef} height={height} naturalHeight={naturalH}>
      <svg ref={svgRef} width={w} height={naturalH} role="img" aria-label={ariaLabel} onMouseLeave={()=>{setHoverIdx(null);hide();}}>
        {data.map((d,i)=>{
          const rowC=pad.t+(i+0.5)*(ch/n), y=rowC-bh/2, isH=hoverIdx===i;
          const bw=Math.max(4,(d.value/max)*cw);
          // Rank degradé: base color for #1, tinting toward white as
          // rank drops (rule 5) — single brand color, no hex ramp.
          const fill=d.color || tintColor(color, n<=1?0:(i/(n-1))*0.72);
          const clickable=typeof onBarClick==='function';
          return(
            <g key={i}
              onMouseMove={e=>{setHoverIdx(i);show(e,`${d.label}: ${format(d.value)}${clickable&&clickHint?`\n${clickHint}`:''}`);}}
              onMouseLeave={()=>{setHoverIdx(null);hide();}}
              {...(clickable ? {
                role:'button', tabIndex:0, 'aria-label':`${d.label}: ${format(d.value)}`, 'data-bar-click':'',
                style:{cursor:'pointer'},
                onClick:()=>onBarClick(d.label,i),
                onKeyDown:e=>{if(e.key==='Enter'||e.key===' '){e.preventDefault();onBarClick(d.label,i);}},
              } : {})}>
              <rect x={0} y={y-5} width={w} height={bh+10} fill="transparent"/>
              {/* Rank chip — en compacto NO se dibuja: el orden de las
                  filas ya ES el ranking, y sus 28px se los devuelve al
                  canal de labels. */}
              {CHIP_W > 0 && (
                <>
                  <rect x={0} y={rowC-CHIP_W/2} width={CHIP_W} height={CHIP_W} rx="6" fill={NEUTRAL.track}/>
                  <text x={CHIP_W/2} y={rowC+3.5} textAnchor="middle" fontSize={D.chipFs} fontWeight="800" fill={NEUTRAL.muted}>{i+1}</text>
                </>
              )}
              {/* Row label — wraps to two end-anchored tspan lines when
                  it exceeds the adaptive channel; ellipsized line 2
                  carries the full label in a native <title>. */}
              {labelRows[i].wrapped ? (
                <text x={pad.l-LABEL_GAP} y={rowC-2.5} textAnchor="end" fontSize={D.wrapFs} fill={NEUTRAL.text} fontWeight={isH?"700":"600"}>
                  {labelRows[i].truncated && <title>{labelRows[i].full}</title>}
                  <tspan x={pad.l-LABEL_GAP} dy="0">{labelRows[i].lines[0]}</tspan>
                  <tspan x={pad.l-LABEL_GAP} dy={D.wrapDy}>{labelRows[i].lines[1]}</tspan>
                </text>
              ) : (
                <text x={pad.l-LABEL_GAP} y={rowC+3.5} textAnchor="end" fontSize={D.labelFs} fill={NEUTRAL.text} fontWeight={isH?"700":"600"}>{labelRows[i].lines[0]}</text>
              )}
              {/* Pill track + ranked pill bar */}
              <rect x={pad.l} y={y} width={cw} height={bh} fill={NEUTRAL.track} rx={rx}/>
              {/* Entrance (ronda 12): the value pill grows from the left
                  edge; track/chip/labels stay outside the wrapper. */}
              <EntranceGroup mode="growX" origin={`${pad.l}px 0px`} disabled={!entrance} drawn={chartDrawn}>
                <rect x={pad.l} y={y} width={bw} height={bh} fill={fill} rx={rx} opacity={isH?1:0.92} style={{transition:'all 0.15s'}}/>
              </EntranceGroup>
              {/* Bold value + share of total */}
              {labelFmt&&<text x={valueX} y={rowC+3.5} textAnchor="end" fontSize={D.valueFs} fill={NEUTRAL.text} fontWeight="700">{labelFmt(d.value)}</text>}
              {labelFmt&&shareTxts[i]!=null&&<text x={shareX} y={rowC+3.5} textAnchor="end" fontSize={D.shareFs} fill={NEUTRAL.muted} fontWeight="600">{shareTxts[i]}</text>}
            </g>
          );
        })}
      </svg>
    </ChartSVGWrap>
  );
}

// ── StackedShareComboT (CHS retailer drill, 2026-07-22) ─────────
//
// "Option D" from Pablo's evaluation widget: ONE chart that shows BOTH
// the weekly authorized mix (100%-stacked columns, colour-blind-friendly
// band pair) and the weekly TOTAL volume (right-axis line) — a 100%
// stack alone hides whether 95% is of 200 listings or of 20,000.
// Designed for ~104 weekly points.
//
// Contract:
// - `data` rows are {label, value, total}: `value` is the AUTHORIZED
//   FRACTION in [0,1] (bottom teal segment; the red top is 1-value),
//   `total` is the week's listing count for the right-axis line.
// - A null/non-finite `value` renders NO column for that week — missing
//   is not zero, same policy as every scorecard primitive.
// - `target` (fraction, e.g. 0.9) draws the dashed threshold on the
//   left axis. The CALLER resolves it (GenericChart derives it from the
//   dashboard's inherited threshold bands) — never hardcoded here.
export function StackedShareComboT({
  data,
  target,
  height = 280,
  width: widthHint = 700,
  goodLabel = 'Authorized',
  badLabel = 'No agreement',
  totalLabel = 'Total listings',
  entrance = true,
  ariaLabel = 'Authorized mix with total volume',
}) {
  const { tip, svgRef, show, hide } = useTooltip();
  const [hoverIdx, setHoverIdx] = useState(null);
  const [wrapRef, w] = useContainerWidth(widthHint);
  // Chart-level grow cycle (r16, migrated 2026-07-24): the CHART owns
  // ONE cycle — first load the columns rise from the axis and the line
  // group fades in a beat behind (unchanged), and every later DATA
  // change replays that same wave together, instead of hard-swapping
  // beside neighbours that re-grow. Signature memoized by data identity
  // (this chart re-renders on every tooltip mousemove — an unmemoized
  // sig would re-hash the whole ~104-week payload per move).
  // Compositor-only; reduced motion / ghosts land settled inside
  // `useGrowCycle`. Hooks live BEFORE the empty-state return.
  const chartDrawn = useGrowCycle(useMemo(() => lineDataSig(data), [data]), !entrance);
  const prm = usePRM();
  // Unique per instance (audit #211): `url(#id)` resolves to the FIRST match
  // in the document, so a fixed id makes a 2nd chart borrow the 1st one's.
  const gid = useId().replace(/[^\w-]/g, '');
  if (!hasData(data)) return <div ref={wrapRef}><ChartEmpty height={height} /></div>;

  const n = data.length;
  const totals = data.map(d => Number(d.total));
  // Right axis: round ticks over the volume domain (rule 14).
  const { ticks: rTicks, max: rMax } = niceTicks(safeMax(totals), 3);
  const h = height, pad = { l: 40, r: 48, t: 14, b: 26 };
  const cw = w - pad.l - pad.r, ch = h - pad.t - pad.b;
  // ComboChartT geometry (Pablo, ronda 8: "adaptemos ese"): slim
  // slot-centred columns with real gaps — width caps at 30px / 55% of
  // the slot, never wall-to-wall fills.
  const bw = cw / n;
  const colW = Math.max(Math.min(bw * 0.55, 30), 2);
  const rx = Math.min(6, Math.max(2, colW / 2));
  const GOOD = SEMANTIC.bandPositive, BAD = SEMANTIC.bandNegative, LINE = PALETTE[1];
  const yOf = frac => pad.t + ch * (1 - frac);
  // Sparse time labels — combo's stride idiom (~6 visible). The hovered
  // bucket always surfaces via the tooltip, so thinning loses nothing.
  const lblStride = xLabelStride(data.map(d => d.label), cw, 6);
  const lblIdx = new Set([0, n - 1]);
  for (let k = lblStride; k < n - 1; k += lblStride) lblIdx.add(k);

  const dotR = lineDotRadius(n);
  // Monotone-cubic segments (no overshoot), with a PEN-UP break at any
  // non-finite total — missing volume is never drawn as zero.
  const lineSegs = [];
  let seg = [];
  data.forEach((d, i) => {
    const t = d.total == null ? NaN : Number(d.total);
    if (!Number.isFinite(t)) {
      if (seg.length) lineSegs.push(seg);
      seg = [];
      return;
    }
    seg.push({ x: pad.l + bw * i + bw / 2, y: yOf(t / rMax) });
  });
  if (seg.length) lineSegs.push(seg);
  const linePath = lineSegs.map(pts => monotonePath(pts)).join(' ');

  const pctFmt = v => `${(v * 100).toFixed(1)}%`;
  // Number(null) is 0 — a null target/value/total must stay NON-finite
  // or the chart fabricates zeros (caught by the vitest pin).
  const num = x => (x == null ? NaN : Number(x));
  const tgt = num(target);

  return (
    <div ref={wrapRef} className="w-full relative" style={{ height: h, overflow: 'hidden' }}>
      <svg ref={svgRef} width={w} height={h} role="img" aria-label={ariaLabel} onMouseLeave={() => { setHoverIdx(null); hide(); }}>
        {/* Left axis: fixed percent gridlines (the domain IS 0..100% —
            a share axis, not a data-max axis, so rule 14's round-tick
            requirement is satisfied by construction). */}
        {[0, 0.25, 0.5, 0.75, 1].map(f => (
          <g key={f}>
            <line x1={pad.l} x2={w - pad.r} y1={yOf(f)} y2={yOf(f)} stroke={NEUTRAL.grid} strokeWidth="0.8" />
            <text x={pad.l - 5} y={yOf(f) + 3} textAnchor="end" fontSize="11" fill={NEUTRAL.faint}>{Math.round(f * 100)}%</text>
          </g>
        ))}
        {/* 100%-stacked columns, rounded via per-column clip so the
            OUTER corners round but the teal/red seam stays crisp —
            same trick as StackedShareBar's whole-bar clip. */}
        <EntranceGroup mode="growY" origin={`0px ${pad.t + ch}px`} disabled={!entrance} drawn={chartDrawn}>
        {data.map((d, i) => {
          const v = num(d.value);
          if (!Number.isFinite(v)) return null;
          const x = pad.l + bw * i + (bw - colW) / 2;
          const seam = yOf(Math.max(0, Math.min(1, v)));
          const isH = hoverIdx === i;
          const cid = `${gid}-sscmb-${i}`;
          return (
            <g key={i} opacity={hoverIdx !== null && !isH ? 0.55 : 1} style={{ transition: 'opacity 0.12s' }}>
              <defs>
                <clipPath id={cid}>
                  <rect x={x} y={pad.t} width={colW} height={ch} rx={rx} ry={rx} />
                </clipPath>
              </defs>
              <g clipPath={`url(#${cid})`}>
                <rect x={x} y={pad.t} width={colW} height={ch} fill={BAD} />
                <rect x={x} y={seam} width={colW} height={pad.t + ch - seam} fill={GOOD} />
              </g>
            </g>
          );
        })}
        </EntranceGroup>
        {/* Target threshold (left axis) — dashed, labelled. */}
        {Number.isFinite(tgt) && (
          <g>
            <line x1={pad.l} x2={w - pad.r} y1={yOf(tgt)} y2={yOf(tgt)} stroke={NEUTRAL.text} strokeWidth="1.4" strokeDasharray="5 4" />
            <text x={pad.l + 4} y={yOf(tgt) - 4} fontSize="11" fontWeight="700" fill={NEUTRAL.text}>{`target ${Math.round(tgt * 100)}%`}</text>
          </g>
        )}
        {/* Total-volume line (right axis) + density-adaptive solid dots
            (rule 15 — no white halo; hover grows the dot). Fades in a
            beat behind the rising columns, on the SAME chart cycle as
            the columns: while `chartDrawn` is false (first load, and the
            one painted frame of a data redraw) the group sits at zero
            WITHOUT a transition — a transition there would animate the
            line backwards on the reset frame, exactly what
            `EntranceGroup`'s controlled mode avoids. */}
        <g
          style={{
            opacity: (!entrance || chartDrawn) ? 1 : 0,
            transition: (prm || !entrance || !chartDrawn)
              ? undefined
              : `opacity ${PM_MOTION.durEntrance}ms ${PM_MOTION.easeOut} ${PM_MOTION.durBase}ms`,
          }}
        >
        <path d={linePath} fill="none" stroke={LINE} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {data.map((d, i) => {
          const t = num(d.total);
          if (!Number.isFinite(t)) return null;
          const x = pad.l + bw * i + bw / 2;
          const y = yOf(t / rMax);
          const isEnd = i === 0 || i === n - 1;
          return <circle key={i} cx={x} cy={y} r={hoverIdx === i ? dotR + 1.6 : lineDotRadius(n, isEnd)} fill={LINE} />;
        })}
        </g>
        {/* Right axis: volume ticks in the line's colour. */}
        {rTicks.map(v => (
          <text key={v} x={w - pad.r + 5} y={yOf(v / rMax) + 3} fontSize="11" fill={LINE} fontWeight="600">{fmtN(v)}</text>
        ))}
        {/* Hover guideline. */}
        {hoverIdx !== null && (
          <line
            x1={pad.l + bw * hoverIdx + bw / 2} x2={pad.l + bw * hoverIdx + bw / 2}
            y1={pad.t} y2={pad.t + ch}
            stroke={NEUTRAL.text} strokeWidth="0.8" strokeDasharray="3 3" opacity="0.5"
          />
        )}
        {/* Full-height hit zones — the hover target is the WEEK, not
            the thin column (rule 11: multi-series tooltip; string
            values because the units are mixed % + count). */}
        {data.map((d, i) => (
          <rect
            key={`hit-${i}`}
            x={pad.l + bw * i} y={pad.t} width={bw} height={ch}
            fill="transparent"
            onMouseMove={e => {
              setHoverIdx(i);
              const v = num(d.value);
              show(e, (
                <MultiSeriesTooltip
                  label={String(d.label ?? '')}
                  showTotal={false}
                  series={[
                    { name: goodLabel, value: Number.isFinite(v) ? pctFmt(v) : '—', color: GOOD },
                    { name: badLabel, value: Number.isFinite(v) ? pctFmt(1 - v) : '—', color: BAD },
                    { name: totalLabel, value: Number.isFinite(num(d.total)) ? fmtN(num(d.total)) : '—', color: LINE },
                  ]}
                />
              ));
            }}
          />
        ))}
        {/* Sparse week labels; the hovered week's label lights up. */}
        {data.map((d, i) => (lblIdx.has(i) || hoverIdx === i) && (
          <text
            key={`xl-${i}`}
            x={pad.l + bw * i + bw / 2} y={h - 8}
            textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'}
            fontSize="11" fill={hoverIdx === i ? NEUTRAL.text : NEUTRAL.faint}
            fontWeight={hoverIdx === i ? '600' : '400'}
          >{String(d.label ?? '')}</text>
        ))}
      </svg>
      <Tooltip tip={tip} svgRef={svgRef} />
    </div>
  );
}
